/**
 * Relay for the native `self_healing:terminal_outcome` event (FR-021a, T044).
 *
 * The stop IPC and this event carry the same typed outcome and land at the
 * same moment, so this is not a latency optimisation. It exists because the
 * IPC is only observable to whoever called it, and the event is not:
 *
 * - When the FE's `stopSession()` exceeds its outer backstop, the native
 *   routine has almost certainly still converged — its own stop deadline
 *   (default 60s) is far below the FE cap. Today that timeout is read as
 *   "unsafe" and routes to the upload-required modal, which is exactly the
 *   "stranded in the upload-your-transcript modal" shape FR-021a says this
 *   feature must eliminate. With the event, the FE can ask what actually
 *   happened instead of inferring failure from its own clock.
 * - A stop the FE never initiated — watchdog, shutdown, another window —
 *   produces no promise to await at all. Only the event reports it.
 *
 * Outcomes are buffered rather than delivered to a live subscriber, because
 * the event can arrive before the awaiter registers (the IPC settles and the
 * emit races the `await`). Keyed by `(recordingId, generation)`: the payload
 * carries no `sessionId`, and the pair is the ownership identity everything
 * else in this feature keys on.
 */
import type { BackupMode, StopReason, TerminalClass } from '@/types/self-healing-stop';

/** The `self_healing:terminal_outcome` payload (camelCase across the IPC). */
export interface NativeTerminalOutcome {
  recordingId: string;
  generation: number;
  reason: StopReason;
  classification: TerminalClass;
  finishSucceeded: boolean;
  failedChunks: number;
  totalChunks: number;
  backupUploaded: boolean;
  filesRetained: boolean;
  recorderStopped: boolean;
  cadenceStopped: boolean;
  workerDrained: boolean;
  durationMs: number;
  backup: BackupMode;
  /**
   * The local offline consult this stop handed its unsent audio to; absent
   * when it did not. A stop that timed out on the FE side still did the
   * handover, so the recovered outcome has to carry it.
   */
  offlineHandoverLocalSessionId?: string;
}

const key = (recordingId: string, generation: number) => `${recordingId}:${generation}`;

/**
 * Bounded so a long-lived desktop process cannot accumulate an entry per
 * recording.
 *
 * ## TWO readers, with two different windows and two different eviction costs
 *
 *  1. **A stop in flight**, through {@link peekTerminalOutcome}. Its window is
 *     exactly one stop: the FE exhausts its `stopSession()` attempts and asks
 *     what actually happened. Eviction costs a LOCAL fallback — that stop reads
 *     its own timeout instead, which is today's behaviour.
 *  2. **The close-guard release gate**, through
 *     {@link peekTerminalOutcomeForRecording}. Its window opens AFTER the stop
 *     has completed and closes when that recording's durable recovery row
 *     settles — seconds to minutes later, longer when the backup upload
 *     retries. Eviction there is NOT local: this buffer holds the only thing
 *     that tells a PAUSED epoch's finished backup from a final one, so a lost
 *     entry makes the gate's per-epoch signal decline. It is not a release on
 *     its own — the gate has three other signals and fails towards not
 *     releasing — but it is the difference between a cheap refusal and leaning
 *     on the session cache and the native recorder.
 *
 * ## 16 is still the right number, and here is the arithmetic
 *
 * `terminal::execute` emits once per epoch and a cached replay emits nothing
 * (FR-006), so one stop produces one entry. Evicting an entry before EITHER
 * reader gets to it therefore needs 16 OTHER recordings to finalize inside that
 * reader's window — 16 stops inside one stop for reader 1, and 16 stops between
 * a pause and its backup settling for reader 2.
 *
 * Reader 2's longer window does not move the order of magnitude, because this
 * `Map` is module-level and dies with the webview. An epoch stopped in a
 * PREVIOUS app launch has no entry to evict: its recovery row finds nothing
 * here by construction, not by eviction. What covers that case is fail-closed,
 * not a lookup elsewhere — the gate once read the server and no longer does
 * (`recording_status` is not a liveness signal on desktop, 2d9053e8), so the
 * relay simply declines to answer and the native recorder and the session cache
 * decide on their own evidence. So the exposure is the same one clinician, in
 * one webview lifetime, producing 16 further stops in the minutes a paused
 * epoch's upload takes to settle. That is still absurd.
 *
 * What WOULD change the answer: a path that finalizes many epochs in a burst
 * inside one webview lifetime — a batched or bulk finalize. Raise it then, and
 * say so here.
 *
 * ## Deliberately NOT aligned with native's `MAX_CACHED_TERMINAL_OUTCOMES` (32)
 *
 * That one is a different mechanism: an idempotency cache, so a repeat
 * `terminal::execute` for an already-finalized epoch replays its stored outcome
 * instead of re-running the workflow, and it has to outlive every actor that
 * might retry. Losing an entry there costs a double-finalize. Matching the
 * numbers would imply a coupling that does not exist and would invite someone
 * to change them together.
 *
 * The two caches also do not interact: native replays from its own map, so
 * evicting here can never deprive a native replay of anything.
 */
const MAX_BUFFERED_OUTCOMES = 16;

const outcomes = new Map<string, NativeTerminalOutcome>();

/** Called by the `self_healing:terminal_outcome` listener. */
export function recordTerminalOutcome(outcome: NativeTerminalOutcome): void {
  if (!outcome?.recordingId) return;
  const id = key(outcome.recordingId, outcome.generation);

  outcomes.set(id, outcome);
  while (outcomes.size > MAX_BUFFERED_OUTCOMES) {
    const oldest = outcomes.keys().next().value;
    if (oldest === undefined) break;
    outcomes.delete(oldest);
  }
}

/**
 * The typed outcome for this epoch, if one has already arrived.
 *
 * Deliberately a lookup rather than a wait. By the time the FE has exhausted
 * its `stopSession()` attempts — a 900s first attempt plus two 15s retries —
 * the native routine is long past its own stop deadline (default 60s), so the
 * outcome has either already been emitted and buffered here or is not coming.
 * Blocking a further few seconds on the chance it lands in that exact window
 * would be a coin flip dressed up as a fix.
 */
export function peekTerminalOutcome(
  recordingId: string,
  generation: number
): NativeTerminalOutcome | undefined {
  return outcomes.get(key(recordingId, generation));
}

/**
 * The latest buffered outcome for a recording id, whatever its generation.
 *
 * Separate from {@link peekTerminalOutcome} because its caller has a different
 * key. A durable recovery row is keyed `(sessionId, recordingId)` and carries
 * no generation, so a releaser holding a row cannot form the `(recordingId,
 * generation)` pair the buffer is keyed by — and the question it asks ("how did
 * THIS epoch end") is answered by any generation of that recording.
 *
 * Latest wins, and "latest" is the HIGHEST GENERATION, not the last entry the
 * iterator hands back. Insertion order would look like it says the same thing
 * and does not: `Map.set` on an existing key updates the value and leaves the
 * entry where it was, so a re-emitted outcome keeps its original position and
 * the last match is not, in general, the most recent stop. Generation is the
 * relay's own ordering for a recording, so it is what is read.
 *
 * An epoch that produced no buffered outcome — evicted, or one app launch ago —
 * returns `undefined`, and the caller is required to treat that as "cannot
 * tell", never as "ended cleanly".
 */
export function peekTerminalOutcomeForRecording(
  recordingId: string
): NativeTerminalOutcome | undefined {
  let latest: NativeTerminalOutcome | undefined;
  for (const outcome of outcomes.values()) {
    if (outcome.recordingId !== recordingId) continue;
    if (!latest || outcome.generation > latest.generation) latest = outcome;
  }
  return latest;
}

/**
 * Whether a typed outcome may advance to note generation (FR-022 / FR-024a).
 *
 * Only the final user stop generates. A pause or device switch finalized a
 * *segment*; every watchdog / shutdown / start-convergence reason is a forced
 * termination on a recording whose source is suspect — auto-generating from
 * either is the SCR-4423 shape this feature exists to close.
 *
 * `feFallback` does NOT generate, and an earlier version of this returning
 * `true` for it was wrong twice over. FR-022 lists it explicitly: note
 * generation fires "only by a final user stop - never by pause, device
 * auto-switch, FE fallback, start-convergence, shutdown, or any watchdog
 * reason". And Rust's `StopReason::triggers_note_generation` is
 * `matches!(self, UserStop)`, so allowing it here split one rule across two
 * halves that disagreed — with the FE half deciding in the direction that
 * generates a note.
 */
export function outcomeMayGenerateNote(reason: StopReason): boolean {
  return reason === 'userStop';
}

/**
 * Project a native terminal outcome onto the shape the existing stop-health
 * gate reads, so recovering from a timeout goes through exactly the same
 * decision as a normally-resolved stop rather than a parallel one.
 */
export function terminalOutcomeAsStopResult(outcome: NativeTerminalOutcome): {
  totalChunks: number;
  totalBytes: number;
  failedChunks: number;
  durationMs: number;
  backupUploaded: boolean;
  finishSucceeded: boolean;
  backupPendingAsync: boolean;
  backupRetained: boolean;
  backupDeferred: boolean;
  offlineHandoverLocalSessionId?: string;
} {
  return {
    totalChunks: outcome.totalChunks,
    totalBytes: 0,
    failedChunks: outcome.failedChunks,
    durationMs: outcome.durationMs,
    backupUploaded: outcome.backupUploaded,
    finishSucceeded: outcome.finishSucceeded,
    backupPendingAsync: outcome.backup === 'pendingAsync',
    backupRetained: outcome.backup === 'retained',
    // A KNOWN GAP, no longer the structural `false` this comment used to
    // claim. Deferral was once produced only by the device-change segment stop
    // (`background_uploads = true`, the one path that rotates the segment
    // WAVs), which this projection never sees — it is read only for an outcome
    // `outcomeMayGenerateNote` admitted, i.e. `userStop`. The detached-backup
    // snapshot changed that: an ordinary `userStop` now relocates its sources
    // too, so a `userStop` recovered here may well have vacated the live paths.
    //
    // `NativeTerminalOutcome` still carries no deferral field, so there is
    // nothing truthful to project, and `false` is kept because it is the only
    // value the existing readers can act on — but it is now the UNSAFE reading
    // on this one path, not the conservative one: it lets the stop flow
    // re-extract from live WAV paths a snapshot may have renamed away. Closing
    // it means widening `TerminalOutcome` in Rust and re-emitting it on the
    // terminal-outcome event; that is a wire-contract change, not a line here.
    backupDeferred: false,
    offlineHandoverLocalSessionId: outcome.offlineHandoverLocalSessionId,
  };
}

/** Test seam — the buffer is module state that would otherwise leak between specs. */
export function __resetTerminalOutcomeRelay(): void {
  outcomes.clear();
}
